import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { Grid, Row, Col, Glyphicon, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import CSV from 'csv-string';
import EventSystem from './EventSystem';
import { Tile, Title, Body } from './style/tile.style';
import GlobalFunction from './globalFunction';
import RmToolTip from './rmToolTip';
import '../node_modules/react-bootstrap-table/dist/react-bootstrap-table-all.min.css';

export default function ScheduleControlModule({ parameters, onSendMessage }) {
    const lsKey = parameters.config.module_name + '_displaySchedule_' + parameters.config.event_screen_module_id;

    const [headingFontSize, setHeadingFontSize] = useState(14);
    const [showModal, setShowModal] = useState(false);
    const [clockLocalOffset] = useState(0);
    const [displaySchedule, setDisplaySchedule] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [fixedTimerActivityLog, setFixedTimerActivityLog] = useState([]);
    const [scheduledTimers, setScheduledTimers] = useState([]);
    const [editDisabled, setEditDisabled] = useState(true);
    const [glyphsDelEditVisible, setGlyphsDelEditVisible] = useState(false);
    const [glyphsCancelVisible, setGlyphsCancelVisible] = useState(false);
    const [glyphsSaveVisible, setGlyphsSaveVisible] = useState(false);
    const [glyphsAddVisible, setGlyphsAddVisible] = useState(true);
    const [hour, setHour] = useState('');
    const [minute, setMinute] = useState('');
    const [second, setSecond] = useState('');
    const [line1, setLine1] = useState('');
    const [line2, setLine2] = useState('');
    const [line3, setLine3] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [dataImported, setDataImported] = useState(false);
    const [importedData, setImportedData] = useState('');
    const [name, setName] = useState('');

    const containerRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const intervalIDRef = useRef(undefined);
    const timerIDRef = useRef(undefined);
    const scheduledTimersRef = useRef([]);

    // Keep ref in sync for interval reads
    scheduledTimersRef.current = scheduledTimers;

    // Stable refs for use in EventSystem callback (subscribed once)
    const liveRef = useRef({ parameters, onSendMessage });
    liveRef.current.parameters = parameters;
    liveRef.current.onSendMessage = onSendMessage;

    const sendNowNextLaterRef = useRef(null);
    sendNowNextLaterRef.current = sendNowNextLater;

    const displayRequestsUpdateStable = useRef(function(data) {
        if (data.event_grouping_seq === liveRef.current.parameters.config.event_grouping_seq) {
            sendNowNextLaterRef.current('display');
        }
    });

    useEffect(() => {
        EventSystem.subscribe('ScheduleDisplayModule', displayRequestsUpdateStable.current);
        window.addEventListener('resize', updateDimensions);
        updateDimensions();
        setName(parameters.config.name);

        const saved = JSON.parse(localStorage.getItem(lsKey));
        if (saved !== null) {
            setDisplaySchedule(saved);
            // loadSchedule triggered by displaySchedule useEffect below
        }

        return () => {
            EventSystem.unsubscribe('ScheduleDisplayModule', displayRequestsUpdateStable.current);
            window.removeEventListener('resize', updateDimensions);
            if (typeof intervalIDRef.current !== 'undefined') clearInterval(intervalIDRef.current);
        };
    }, []);

    // Load schedule whenever displaySchedule is saved/initialized
    const firstDisplayScheduleRender = useRef(true);
    useEffect(() => {
        if (firstDisplayScheduleRender.current) { firstDisplayScheduleRender.current = false; return; }
        loadSchedule(displaySchedule);
    }, [displaySchedule]);

    function updateDimensions() {
        if (!containerRef.current || !fontSizeCheckerRef.current) return;
        const headingWidth = containerRef.current.clientWidth - 40;
        let fs = GlobalFunction.getFontSize(parameters.config.description, headingWidth, 30, fontSizeCheckerRef.current);
        if (fs > 22) fs = 22;
        setHeadingFontSize(fs);
    }

    function clearEditRow() {
        setHour(''); setMinute(''); setSecond('');
        setLine1(''); setLine2(''); setLine3('');
    }

    function editRow() {
        setGlyphsDelEditVisible(false);
        setGlyphsCancelVisible(true);
        setEditDisabled(false);
    }

    function editSelectedRow(row, isSelected) {
        if (isSelected) {
            setSelectedRows([row.row]);
            setHour(row.hour); setMinute(row.minute); setSecond(row.second);
            setLine1(row.line1); setLine2(row.line2); setLine3(row.line3);
            setGlyphsDelEditVisible(true);
            setGlyphsCancelVisible(false);
            setGlyphsSaveVisible(false);
            setGlyphsAddVisible(false);
        } else {
            clearEditRow();
            setGlyphsDelEditVisible(false);
            setGlyphsCancelVisible(false);
            setGlyphsSaveVisible(false);
            setGlyphsAddVisible(true);
            setEditDisabled(true);
            setSelectedRows([]);
        }
    }

    function deleteRow() {
        const newSchedule = displaySchedule
            .filter(r => r.row !== selectedRows[0])
            .map(r => {
                let updated = r;
                if (r.childRow === selectedRows[0]) updated = { ...r, childRow: 0 };
                if (r.parentRow === selectedRows[0]) updated = { ...r, parentRow: 0 };
                return updated;
            });

        if (newSchedule.length === 0 && typeof intervalIDRef.current !== 'undefined') {
            clearInterval(intervalIDRef.current);
        }

        setDisplaySchedule(newSchedule);
        saveToLocalStorage(newSchedule);
        setSelectedRows([]);
        clearEditRow();
        setGlyphsDelEditVisible(false);
        setGlyphsSaveVisible(false);
        setGlyphsCancelVisible(false);
        setEditDisabled(true);
        setGlyphsAddVisible(true);
    }

    function addRow() {
        setSelectedRows([]);
        clearEditRow();
        setHour('0'); setMinute('0'); setSecond('0');
        setLine1(''); setLine2(''); setLine3('');
        setGlyphsDelEditVisible(false);
        setGlyphsSaveVisible(false);
        setGlyphsCancelVisible(true);
        setEditDisabled(false);
        setGlyphsAddVisible(false);
    }

    function saveRow() {
        let updatedExistingRow = false;
        let highwaterRow = 0;
        const newSchedule = displaySchedule.map(r => {
            if (r.row > highwaterRow) highwaterRow = r.row;
            if (r.row === selectedRows[0]) {
                updatedExistingRow = true;
                return { row: selectedRows[0], hour, minute, second, line1, line2, line3 };
            }
            let updated = r;
            if (r.parentRow === selectedRows[0]) updated = { ...r, parentRow: 0 };
            return updated;
        });

        if (!updatedExistingRow) {
            newSchedule.push({ row: highwaterRow + 1, hour, minute, second, line1, line2, line3 });
        }

        setDisplaySchedule(newSchedule);
        saveToLocalStorage(newSchedule);
        setSelectedRows([]);
        clearEditRow();
        setGlyphsDelEditVisible(false);
        setGlyphsSaveVisible(false);
        setGlyphsCancelVisible(false);
        setEditDisabled(true);
        setGlyphsAddVisible(true);
    }

    function undoRow() {
        setEditDisabled(true);
        clearEditRow();
        setGlyphsDelEditVisible(false);
        setGlyphsSaveVisible(false);
        setGlyphsCancelVisible(false);
        setSelectedRows([]);
        setGlyphsAddVisible(true);
    }

    function handleFixedChangeBlur(e) {
        if (e.target.name === 'hour' && hour.length === 0) setHour('*');
    }

    function handleFixedChange(e) {
        const { name: fieldName, value } = e.target;
        if (fieldName === 'hour') {
            if (value.length === 0 || value === '*') { setHour(value); clearErrorMessage(); return; }
            if (validNumber(value, 23, 'hours', 'Hours')) { setHour(parseInt(value, 10)); clearErrorMessage(); }
        } else if (fieldName === 'minute') {
            if (value === '') { setMinute(value); return; }
            if (validNumber(value, 59, 'minutes', 'Minutes')) { setMinute(parseInt(value, 10)); clearErrorMessage(); }
        } else if (fieldName === 'second') {
            if (value === '') { setSecond(value); return; }
            if (validNumber(value, 59, 'seconds', 'Seconds')) { setSecond(parseInt(value, 10)); clearErrorMessage(); }
        } else if (fieldName === 'line1') { setLine1(value); clearErrorMessage(); }
        else if (fieldName === 'line2') { setLine2(value); clearErrorMessage(); }
        else if (fieldName === 'line3') { setLine3(value); clearErrorMessage(); }
    }

    function validNumber(dataField, maxNumber, fieldUnit, fieldLabel) {
        const minAmount = fieldLabel === 'Duration' ? 1 : 0;
        const parsed = parseInt(dataField, 10);
        if (Number.isNaN(parsed) || parsed > maxNumber || parsed < minAmount) {
            showErrorMessage(fieldLabel + ' must be a number between ' + minAmount + ' and ' + maxNumber + ' ' + fieldUnit);
            return false;
        }
        return true;
    }

    function saveToLocalStorage(schedule) {
        localStorage.setItem(lsKey, JSON.stringify(schedule));
    }

    function showErrorMessage(message) {
        if (timerIDRef.current) clearTimeout(timerIDRef.current);
        setErrorMessage(message);
        timerIDRef.current = setTimeout(() => setErrorMessage(''), 3000);
    }

    function clearErrorMessage() {
        clearTimeout(timerIDRef.current);
        setErrorMessage('');
        setGlyphsSaveVisible(true);
    }

    function convertFixedToSchedule(item) {
        const runTime = moment().hour(parseInt(item.hour, 10)).minute(parseInt(item.minute, 10)).second(parseInt(item.second, 10));
        return {
            expired: runTime < moment(),
            runTime,
            line1: item.line1,
            line2: item.line2,
            line3: item.line3,
        };
    }

    function loadSchedule(schedule) {
        if (typeof intervalIDRef.current !== 'undefined') clearInterval(intervalIDRef.current);
        let timers = [];
        schedule.forEach(item => {
            if (item.hour !== '*') {
                timers.push(convertFixedToSchedule(item));
            } else {
                for (let hh = 0; hh < 24; hh++) {
                    timers.push(convertFixedToSchedule({ ...item, hour: hh }));
                }
            }
        });
        if (timers.length > 1) timers.sort((a, b) => a.runTime - b.runTime);
        timers = timers.map((t, i) => ({ ...t, row: i }));
        scheduledTimersRef.current = timers;
        setScheduledTimers(timers);
        scheduler(timers);
    }

    function getEventTime() {
        const d = new Date();
        d.setMilliseconds(d.getMilliseconds() + parameters.config.clockOffset - parameters.config.timeMasterClockOffset);
        d.setSeconds(d.getSeconds() + clockLocalOffset);
        return d;
    }

    function getFormattedTimerRow(dataRow) {
        if (typeof dataRow === 'undefined') return undefined;
        return { runTime: dataRow.runTime, line1: dataRow.line1, line2: dataRow.line2, line3: dataRow.line3 };
    }

    function sendNowNextLater(logSource, timersOverride) {
        const cfg = liveRef.current.parameters.config;
        const timers = timersOverride ?? scheduledTimersRef.current;
        const msg = {
            sourceModuleName: cfg.module_name,
            event_grouping_seq: cfg.event_grouping_seq,
            customerID: liveRef.current.parameters.userProfile.customer_id,
            siteID: liveRef.current.parameters.userProfile.site_id,
            eventID: cfg.event_id,
        };

        const nowNextLater = [{}, {}, {}];
        let found = false;
        for (let x = 0; x < timers.length; x++) {
            if (!timers[x].expired) {
                nowNextLater[0] = getFormattedTimerRow(timers[x - 1]) ?? {};
                nowNextLater[1] = getFormattedTimerRow(timers[x]);
                nowNextLater[2] = getFormattedTimerRow(timers[x + 1]) ?? {};
                found = true;

                msg.nowNextLater = nowNextLater;
                liveRef.current.onSendMessage(msg);

                if (logSource !== 'display') {
                    let logString = '';
                    const fmt = (row) => row && row.runTime ? moment(row.runTime).format('H:mm') + ' - ' + row.line1 : '';
                    if (cfg.other_config.currentItemLabel.length > 0) logString += cfg.other_config.currentItemLabel + ': ' + fmt(nowNextLater[0]);
                    if (cfg.other_config.nextItemLabel.length > 0) logString += (logString.length > 0 ? ' / ' : '') + cfg.other_config.nextItemLabel + ': ' + fmt(nowNextLater[1]);
                    if (cfg.other_config.nextNextItemLabel.length > 0) logString += (logString.length > 0 ? ' / ' : '') + cfg.other_config.nextNextItemLabel + ': ' + fmt(nowNextLater[2]);
                    addToLog(logString);
                }
                break;
            }
        }

        if (!found) {
            addToLog('Nothing scheduled for the rest of the day');
            msg.nowNextLater = [{}, {}, {}];
            liveRef.current.onSendMessage(msg);
        }
    }

    function scheduler(timers) {
        addToLog('Schedule reloaded');
        sendNowNextLater('scheduler', timers);

        intervalIDRef.current = setInterval(() => {
            const currentEventDate = getEventTime();
            const current = scheduledTimersRef.current;
            let changed = false;
            const newTimers = current.map(t => {
                if (!t.expired && currentEventDate - t.runTime > -100) {
                    changed = true;
                    return { ...t, expired: true };
                }
                return t;
            });
            if (changed) {
                scheduledTimersRef.current = newTimers;
                setScheduledTimers(newTimers);
                sendNowNextLater('scheduler', newTimers);
            }
        }, 100);
    }

    function addToLog(activity) {
        setFixedTimerActivityLog(prev => [...prev, {
            utcTime: getEventTime(),
            row: prev.length,
            activity,
        }]);
    }

    function processImportedData() {
        CSV.readAll(importedData, (row) => {
            let err = '';
            for (let x = 0; x < row.length; x++) {
                if (row[x].length !== 6) err += 'Row ' + (x + 1) + ' contains ' + row[x].length + ' fields, expecting 6. ';
            }
            if (err.length > 0) { alert(err); return; }

            const newSchedule = row.map((r, i) => ({
                row: i + 1,
                hour: parseInt(r[0], 10),
                minute: parseInt(r[1], 10),
                second: parseInt(r[2], 10),
                line1: r[3].replace(/'/gi, ''),
                line2: r[4].replace(/'/gi, ''),
                line3: r[5].replace(/'/gi, ''),
            }));
            setDisplaySchedule(newSchedule);
            saveToLocalStorage(newSchedule);
        });
    }

    function fileSelected(e) {
        setDataImported(true);
        const reader = new FileReader();
        reader.onload = (event) => setImportedData(event.target.result);
        reader.readAsText(e.target.files[0]);
    }

    const eid = parameters.config.event_grouping_seq;
    let glyphsSaveVisibleComputed = glyphsSaveVisible;
    const errors = validate(hour, minute, second, line1, displaySchedule, selectedRows[0]);
    if (errors.hour || errors.minute || errors.second || errors.line1) glyphsSaveVisibleComputed = false;
    let message = errorMessage.length > 0 ? errorMessage : errors.message;
    if (editDisabled) { message = ''; }

    const rowStyle = { height: parameters.containerHeight.container + 'px' };
    const selectRowProp = {
        mode: 'radio', clickToSelect: true, bgColor: 'yellow', hideSelectColumn: true,
        onSelect: editSelectedRow, selected: selectedRows,
    };

    const headStyle = { paddingLeft: '6px', paddingRight: '2px' };
    const colStyle = { paddingLeft: '6px', paddingRight: '3px' };
    const footerColStyle = { paddingLeft: '6px', paddingRight: '6px', textAlign: 'left' };
    const inputStyle = { width: '100%', paddingLeft: '6px', paddingRight: '6px' };

    const editGlyphStyle = { paddingTop: '5px', paddingBottom: '5px', width: '45%', color: 'black', fontSize: 'large', display: glyphsDelEditVisible ? 'inline-block' : 'none' };
    const saveGlyphStyle = { paddingTop: '5px', paddingBottom: '5px', width: '45%', color: 'green', fontSize: 'large', display: glyphsSaveVisibleComputed ? 'inline-block' : 'none' };
    const canGlyphStyle = { paddingTop: '5px', paddingBottom: '5px', width: '45%', color: 'red', fontSize: 'large', display: glyphsCancelVisible ? 'inline-block' : 'none' };
    const addGlyphStyle = { paddingTop: '5px', paddingBottom: '5px', width: '45%', color: 'green', fontSize: 'large', display: glyphsAddVisible ? 'inline-block' : 'none' };
    const timeGlyphStyle = { position: 'absolute', top: '0', right: 0, height: '30px', width: '35px', paddingTop: '7px', fontSize: '22px' };
    const optionStyle = { borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66', borderRadius: 0 };
    const bodyHeight = parameters.containerHeight.container - 2 - 37 - 2;

    const options = fixedTimerActivityLog.length > 0
        ? fixedTimerActivityLog.slice().reverse().map((entry, i) => (
            <li key={'Option' + i} style={optionStyle}>{entry.activity}</li>
        ))
        : [];

    return (
        <Tile className="outer-container" style={rowStyle} ref={containerRef}>
            <Modal show={showModal} autoFocus>
                <Modal.Header>
                    <Modal.Title>{name}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ paddingLeft: '7px', paddingRight: '7px' }}>
                    <Grid>
                        <Row>
                            <Col xs={12} sm={9} md={7} lg={6}>
                                <Row>
                                    <BootstrapTable data={displaySchedule} selectRow={selectRowProp} striped hover height='280px'>
                                        <TableHeaderColumn hidden isKey={true} dataField='row'>row</TableHeaderColumn>
                                        <TableHeaderColumn width="7%" dataField='hour'>HH</TableHeaderColumn>
                                        <TableHeaderColumn width="5%" dataField='minute'>MI</TableHeaderColumn>
                                        <TableHeaderColumn width="5%" dataField='second'>SS</TableHeaderColumn>
                                        <TableHeaderColumn width="25%" dataField='line1'>Line 1</TableHeaderColumn>
                                        <TableHeaderColumn width="20%" dataField='line2'>Line 2</TableHeaderColumn>
                                        <TableHeaderColumn width="20%" dataField='line3'>Line 3</TableHeaderColumn>
                                    </BootstrapTable>
                                </Row>
                                <Row><br /></Row>
                                <Row>
                                    <Col xs={1} style={headStyle}>Hour</Col>
                                    <Col xs={1} style={headStyle}>Min</Col>
                                    <Col xs={1} style={headStyle}>Sec</Col>
                                    <Col xs={3} style={headStyle}>Line 1</Col>
                                    <Col xs={2} style={headStyle}>Line 2</Col>
                                    <Col xs={2} style={headStyle}>Line 3</Col>
                                </Row>
                                <Row>
                                    <Col xs={1} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled} name="hour" autoFocus
                                            className={errors.hour ? 'form-control input-md error' : 'form-control input-md'}
                                            value={hour} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                    </Col>
                                    <Col xs={1} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled} name="minute"
                                            className={errors.minute ? 'form-control input-md error' : 'form-control input-md'}
                                            value={minute} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                    </Col>
                                    <Col xs={1} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled} name="second"
                                            className={errors.second ? 'form-control input-md error' : 'form-control input-md'}
                                            value={second} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                    </Col>
                                    <Col xs={3} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled} name="line1"
                                            className={errors.line1 ? 'form-control input-md error' : 'form-control input-md'}
                                            value={line1} onChange={handleFixedChange} onBlur={handleFixedChangeBlur} />
                                    </Col>
                                    <Col xs={2} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled}
                                            className="form-control input-md" name="line2" value={line2} onChange={handleFixedChange} />
                                    </Col>
                                    <Col xs={2} style={colStyle}>
                                        <input type="text" style={inputStyle} disabled={editDisabled}
                                            className="form-control input-md" name="line3" value={line3} onChange={handleFixedChange} />
                                    </Col>
                                    <Col xs={2} style={colStyle}>
                                        <OverlayTrigger placement="left" overlay={<Tooltip id={'deleteRow' + eid}>Delete timer</Tooltip>}>
                                            <Glyphicon onClick={deleteRow} className="cog" style={editGlyphStyle} glyph="trash" />
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="right" overlay={<Tooltip id={'editRow' + eid}>Edit timer</Tooltip>}>
                                            <Glyphicon onClick={editRow} className="cog" style={editGlyphStyle} glyph="edit" />
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="left" overlay={<Tooltip id={'undoChanges' + eid}>Undo Changes</Tooltip>}>
                                            <Glyphicon onClick={undoRow} className="cog" style={canGlyphStyle} glyph="remove" />
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="right" overlay={<Tooltip id={'saveChanges' + eid}>Save Changes</Tooltip>}>
                                            <Glyphicon onClick={saveRow} className="cog" style={saveGlyphStyle} glyph="ok" />
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="left" overlay={<Tooltip id={'addRow' + eid}>Add new timer</Tooltip>}>
                                            <Glyphicon onClick={addRow} className="cog" style={addGlyphStyle} glyph="plus" />
                                        </OverlayTrigger>
                                    </Col>
                                </Row>
                                <Row>
                                    <div style={{ color: 'red', height: '20px' }}>{message}</div>
                                </Row>
                            </Col>
                        </Row>
                    </Grid>
                </Modal.Body>
                <Modal.Footer>
                    <Row>
                        <Col xs={1} style={footerColStyle} />
                        <Col xs={6} style={footerColStyle}>
                            Import Data (Erases existing schedule)
                            <input type="file" id="importfile" name="imageFile" accept=".csv" onChange={fileSelected} />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={1} style={footerColStyle} />
                        <Col xs={6} style={footerColStyle}>
                            <Button name="import" bsStyle="primary" disabled={!dataImported} onClick={processImportedData}>Import</Button>
                        </Col>
                        <Col xs={3}>
                            <Button name="cancel" bsStyle="danger"
                                disabled={glyphsCancelVisible || glyphsSaveVisibleComputed}
                                onClick={() => { setShowModal(false); loadSchedule(displaySchedule); }}>
                                Exit Configuration
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Modal>

            <Title fontSize={headingFontSize + 'px'}>
                <span style={{ paddingLeft: '5px' }}>{name}</span>
                <RmToolTip itemText="schedule" itemType="icon" placement="bottom"
                    onClick={() => {
                        if (typeof intervalIDRef.current !== 'undefined') clearInterval(intervalIDRef.current);
                        setShowModal(true);
                    }}
                    style={timeGlyphStyle}
                    toolTipText={<span>Configure<br />Schedule</span>} />
            </Title>
            <Body height={bodyHeight + 'px'}>
                <ul name="logInformation" style={{
                    marginLeft: '10px', width: 'calc(100% - 10px)', paddingRight: '10px',
                    borderStyle: 'none', overflowX: 'hidden', overflowY: 'auto',
                    listStyle: 'none', paddingLeft: 0, height: '100%',
                    backgroundColor: parameters.colours.backgroundColor,
                    color: parameters.colours.color,
                }} size="30">
                    {options}
                </ul>
            </Body>

            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }} ref={fontSizeCheckerRef} />
        </Tile>
    );
}

function validate(hour, minute, second, line1, displaySchedule, selectedRow) {
    let message = '';
    let hourError = false, minuteError = false, secondError = false, line1Error = false;

    if (line1.length === 0) { line1Error = true; message += 'Line1 cannot be empty. '; }
    if (String(minute).length === 0) { minuteError = true; message += 'Minute cannot be empty. '; }
    if (String(second).length === 0) { secondError = true; message += 'Second cannot be empty. '; }
    if (hour === '*' && parseInt(minute, 10) === 0 && parseInt(second, 10) === 0) {
        minuteError = true; secondError = true;
        message += 'Minute and Second cannot be 0 for hourly timers. ';
    }

    for (let x = 0; x < displaySchedule.length; x++) {
        if (displaySchedule[x].row !== selectedRow) {
            if (displaySchedule[x].hour == hour && displaySchedule[x].minute == minute && displaySchedule[x].second == second) {
                hourError = true; minuteError = true; secondError = true;
                message += 'Duplicate schedule time not allowed. ';
            }
        }
    }

    if (message.length === 0) {
        if (hour === '*') {
            message = second === '0' || second === 0
                ? 'Timer will run hourly at ' + minute + ' minutes exactly past the hour'
                : 'Timer will run hourly at ' + minute + ' minutes and ' + second + ' seconds past the hour';
        } else {
            message = 'Timer will run once at ' + hour + ':' +
                (parseInt(minute, 10) < 10 ? '0' + minute : minute) + ':' +
                (parseInt(second, 10) < 10 ? '0' + second : second);
        }
    }

    return { hour: hourError, minute: minuteError, second: secondError, line1: line1Error, message };
}
